import { forwardRef } from "react";
import { cn } from "../lib/cn";
import { useFieldControlProps } from "../molecules/Field";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** Error state — error border + aria-invalid. */
  invalid?: boolean;
  /** Element rendered inside the input box, before the text field. */
  startAdornment?: React.ReactNode;
  /** Element rendered inside the input box, after the text field. */
  endAdornment?: React.ReactNode;
  /** Height — sm 32px, md 40px (default), lg 48px. */
  inputSize?: "sm" | "md" | "lg";
}

/** Box chrome shared by the bare input and the adornment wrapper. */
const boxClasses =
  "box-border w-full rounded-lg border border-border-control bg-surface-default px-3 transition-colors";

/** Typography + placeholder + disabled styling, always on the <input> itself. */
const textClasses =
  "type-body-lg text-content-primary placeholder:text-content-disabled disabled:cursor-not-allowed disabled:opacity-38";

const sizeClasses: Record<NonNullable<InputProps["inputSize"]>, string> = {
  sm: "h-8",
  md: "h-10",
  lg: "h-12",
};

/** Adornment wrapper — focus ring follows the focused inner input. */
const focusWithinClasses =
  "focus-within:border-border-focused focus-within:ring-2 focus-within:ring-border-focused-subtle";

/**
 * Text input with optional start/end adornments. The wrapper remains mounted
 * when adornments change so the focused input is never replaced.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      invalid = false,
      startAdornment,
      endAdornment,
      inputSize = "md",
      id,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      ...props
    },
    ref,
  ) => {
    const fieldControlProps = useFieldControlProps({
      id,
      describedBy: ariaDescribedBy,
      invalid: ariaInvalid || invalid,
      required: ariaRequired,
    });
    const resolvedInvalid = Boolean(fieldControlProps["aria-invalid"]);
    const invalidClasses = resolvedInvalid
      ? "border-surface-error focus-visible:border-surface-error focus-within:border-surface-error"
      : "";

    return (
      <div
        data-slot="input-wrapper"
        className={cn(
          boxClasses,
          focusWithinClasses,
          "flex items-center gap-2",
          sizeClasses[inputSize],
          invalidClasses,
          className,
        )}
      >
        {startAdornment ? (
          <span className="flex shrink-0 items-center whitespace-nowrap">
            {startAdornment}
          </span>
        ) : null}
        <input
          ref={ref}
          data-slot="input"
          {...fieldControlProps}
          className={cn(
            "min-w-0 grow bg-transparent p-0 outline-none",
            textClasses,
          )}
          {...props}
        />
        {endAdornment ? (
          <span className="flex shrink-0 items-center whitespace-nowrap">
            {endAdornment}
          </span>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";
